import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import { Toast, showToast } from '@evogard/ui';
import {
  TICKET_OPENED_EVENT,
  type TicketOpenedNotice,
  TICKET_REPLIED_EVENT,
  type TicketRepliedNotice,
  PAYMENT_UPDATED_EVENT,
  type PaymentUpdatedNotice,
  BOLETO_PAID_EVENT,
  type BoletoPaidNotice,
  EXPENSE_PENDING_EVENT,
  type ExpensePendingNotice,
  BREAK_OVERRUN_EVENT,
  type BreakOverrunNotice,
  BREAK_CLOSED_EVENT,
  BREAK_ADJUSTMENT_REQUESTED_EVENT,
  type BreakAdjustmentRequestedNotice,
} from '@evogard/contract';
import { useSessionContext } from './session.js';
import { dispatchRealtime, setRealtimeConnected } from './realtime-bus.js';
import { NOTIFICATIONS_KEY } from './notifications.js';
import { playNotificationSound } from './sound.js';
import { BALANCE_KEY } from '../components/BalanceWidget.js';
import { BREAK_OVERRUNS_KEY } from '../components/BreakOverrunsBadge.js';

/**
 * O pulso do pagamento: um número que muda a cada aviso de pagamento que chega
 * pelo WebSocket. O quadro o põe na `reloadKey` do `Kanban` e relê as colunas.
 */
export const PAYMENTS_PULSE_KEY = ['management', 'payments-pulse'] as const;

/** O título do popup, pelo tipo do aviso. O que não está aqui usa o genérico. */
// @valor
const PAYMENT_NOTICE_TITLES: Record<string, string> = {
  correcao_solicitada: 'Nota devolvida para correção',
  nf_aprovada: 'Nota aprovada',
  premiacao_paga: 'Premiação paga',
  pagamento_recusado: 'Pagamento recusado',
  observacao_adicionada: 'Nova observação do Financeiro',
  lote_fechado: 'Lote de premiação fechado',
};

/**
 * A conexão de tempo real — uma por sessão, montada uma vez em `Layout.tsx`.
 *
 * ## Por que reconecta ao trocar de base
 *
 * O `tenantId` vai no handshake (`auth.tenantId`), porque é o servidor quem
 * decide as salas — nunca o cliente (`RealtimeGateway`, no back-end). Trocar
 * de base muda QUAIS salas valem, e a única forma honesta de trocar de sala
 * é uma conexão nova: o efeito depende de `tenantId`, então o React já
 * desconecta a antiga e abre a nova sozinho quando ele muda.
 *
 * ## Por que não é o mesmo cliente do `@evogard/client`
 *
 * O ts-rest fala HTTP; isto é outro protocolo, com outro ciclo de vida
 * (conecta uma vez, fica aberto, reconecta sozinho na queda). Import
 * `socket.io-client` fica só aqui — nenhuma tela abre socket por conta
 * própria.
 */
export function useRealtimeNotifications(): void {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const tenantId = context?.currentTenant.id;

  /*
   * A preferência do som num `ref`, e não na lista do efeito: trocá-la no menu
   * da conta não pode derrubar e reabrir o WebSocket — só muda se o próximo
   * aviso toca.
   */
  const sound = useRef(true);
  sound.current = context?.preferences.notificationSound ?? true;

  useEffect(() => {
    if (!tenantId) return;

    const ring = () => {
      if (sound.current) playNotificationSound();
    };

    const socket = io({ auth: { tenantId } });

    /*
     * O servidor derruba esta conexão de propósito quando algo muda embaixo
     * dela — saiu do departamento, foi bloqueado, o grupo foi editado
     * (`RealtimeService.forceReconnect`, no back-end). O Socket.IO só
     * reconecta SOZINHO quando a queda NÃO veio do servidor; para esta —
     * `reason === 'io server disconnect'` —, `socket.active` fica falso e
     * ninguém tenta de novo sem este `connect()` explícito. Sem ele,
     * "derrubar pra reautorizar" vira só "derrubar": a aba fica muda até a
     * pessoa recarregar.
     */
    socket.on('disconnect', (reason) => {
      setRealtimeConnected(false);
      if (reason === 'io server disconnect') socket.connect();
    });

    /*
     * `pronto`, e não `connect`: o transporte abre antes de o servidor decidir
     * as salas, e uma tela que afrouxasse o polling no `connect` ficaria
     * alguns instantes sem aviso e sem polling.
     */
    socket.on('pronto', () => setRealtimeConnected(true));

    /* Os avisos que os módulos registram (`realtime-bus.ts`). */
    socket.onAny((event: string, payload: unknown) =>
      dispatchRealtime(event, payload, client),
    );

    socket.on(TICKET_OPENED_EVENT, (notice: TicketOpenedNotice) => {
      ring();
      showToast((dismiss) => (
        <Toast title="Novo ticket" icon="sino" onClose={dismiss}>
          <p className="text-sm">
            <span className="font-mono">{notice.ticketCode}</span> em {notice.department}{' '}
            — aberta por {notice.openedBy}
          </p>
        </Toast>
      ));
      // best-effort: o histórico do sino reflete ao vivo, sem esperar reabrir o menu
      void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    });

    socket.on(TICKET_REPLIED_EVENT, (notice: TicketRepliedNotice) => {
      ring();
      showToast((dismiss) => (
        <Toast title="Nova resposta" icon="sino" onClose={dismiss}>
          <p className="text-sm">
            <span className="font-mono">{notice.ticketCode}</span> — {notice.title} —
            respondida por {notice.repliedBy}
          </p>
        </Toast>
      ));
      void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    });

    /*
     * O pagamento da premiação mudou — devolvido, aprovado, pago, uma
     * observação nova. O popup diz a frase do aviso; as consultas de
     * pagamento são invalidadas para o painel, a tabela e o cartão aberto
     * mostrarem a fase nova sem recarregar, e o pulso faz o quadro reler as
     * colunas (o `Kanban` pagina sozinho e não é uma consulta do cache).
     */
    socket.on(PAYMENT_UPDATED_EVENT, (notice: PaymentUpdatedNotice) => {
      ring();
      showToast((dismiss) => (
        <Toast
          title={PAYMENT_NOTICE_TITLES[notice.type] ?? 'Pagamento da premiação'}
          icon="sino"
          onClose={dismiss}
        >
          <p className="text-sm">{notice.subject}</p>
        </Toast>
      ));
      void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      void client.invalidateQueries({ queryKey: ['management', 'payments'] });
      /* A baixa zera o lote no "Meu saldo" do topo — ver `BalanceWidget`. */
      void client.invalidateQueries({ queryKey: BALANCE_KEY });
      client.setQueryData(PAYMENTS_PULSE_KEY, Date.now());
    });

    /*
     * Um associado da carteira do consultor pagou — a frase já traz placa,
     * nome e valor (`paidBoletoSubject`, no back-end). Não há tela de destino:
     * o aviso é a notícia inteira, e o sino guarda o histórico.
     */
    /*
     * O Financeiro pôs em pendência uma despesa que ESTA pessoa aprovou
     * (28/10/2026): só chega ao líder que aprovou. O popup traz o motivo, e os
     * quadros do supervisor — os do sinistro e o da assistência — releem, para
     * o cartão aparecer em Pendente sem recarregar.
     */
    socket.on(EXPENSE_PENDING_EVENT, (notice: ExpensePendingNotice) => {
      ring();
      showToast((dismiss) => (
        <Toast title="Pendência do Financeiro" icon="pausa" onClose={dismiss}>
          <p className="text-sm">{notice.subject}</p>
        </Toast>
      ));
      void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      void client.invalidateQueries({ queryKey: ['claims', 'expenses'] });
      void client.invalidateQueries({ queryKey: ['assistance', 'expenses'] });
    });

    socket.on(BOLETO_PAID_EVENT, (notice: BoletoPaidNotice) => {
      ring();
      showToast((dismiss) => (
        <Toast title="Boleto pago" icon="sino" onClose={dismiss}>
          <p className="text-sm">{notice.subject}</p>
        </Toast>
      ));
      void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    });

    /*
     * Alguém não encerrou o intervalo até 1 minuto depois do fim previsto —
     * só chega a quem tem `breaks.overruns:ver`. O som toca uma vez por
     * estouro; o botão vermelho do topo e a lista releem.
     */
    socket.on(BREAK_OVERRUN_EVENT, (notice: BreakOverrunNotice) => {
      ring();
      showToast((dismiss) => (
        <Toast title="Intervalo estourado" icon="relogio" onClose={dismiss}>
          <p className="text-sm">{notice.subject}</p>
        </Toast>
      ));
      void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      void client.invalidateQueries({ queryKey: BREAK_OVERRUNS_KEY });
    });

    /* Alguém da equipe (ou da base, sem líder) pediu ajuste de horário. */
    socket.on(
      BREAK_ADJUSTMENT_REQUESTED_EVENT,
      (notice: BreakAdjustmentRequestedNotice) => {
        ring();
        showToast((dismiss) => (
          <Toast title="Ajuste de intervalo" icon="relogio" onClose={dismiss}>
            <p className="text-sm">{notice.subject}</p>
          </Toast>
        ));
        void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
        void client.invalidateQueries({ queryKey: ['intervalo'] });
      },
    );

    /* Encerrou, foi ajustado ou dispensado: sai do botão vermelho. */
    socket.on(BREAK_CLOSED_EVENT, () => {
      void client.invalidateQueries({ queryKey: BREAK_OVERRUNS_KEY });
    });

    return () => {
      setRealtimeConnected(false);
      socket.disconnect();
    };
  }, [tenantId, client]);
}
